import React, {useCallback, useEffect, useState} from 'react';
import createAxiosInstance from "../support/AxiosSupport";
import {pluralItems, pluralLists} from '../utils/plural';
import {useToast} from '../context/ToastContext';
import {hasPermission, useWardrobe} from "../context/WardrobeContext";
import Spinner from "./Spinner";
import Dialog from "./Dialog";
import {PlusIcon} from "./icons";

interface ClothingListBrief {
    id: string;
    name: string;
}

interface BulkAddToListsModalProps {
    isOpen: boolean;
    onClose: () => void;
    itemIds: string[];
    onSuccess: () => void;
    ownerId?: string | null;
}

const BulkAddToListsModal: React.FC<BulkAddToListsModalProps> = ({
    isOpen,
    onClose,
    itemIds,
    onSuccess,
    ownerId
}) => {
    const {isOwnWardrobe, permissions} = useWardrobe();
    const canCreateList = isOwnWardrobe || hasPermission(permissions, 'MANAGE_LISTS');
    const {showToast} = useToast();
    const [allLists, setAllLists] = useState<ClothingListBrief[]>([]);
    const [selectedListIds, setSelectedListIds] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    const [newName, setNewName] = useState('');
    const [newDescription, setNewDescription] = useState('');
    const [creating, setCreating] = useState(false);

    const fetchLists = useCallback(async () => {
        setLoading(true);
        try {
            const api = await createAxiosInstance();
            const res = await api.get<ClothingListBrief[]>('/lists', {params: ownerId ? {ownerId} : {}});
            setAllLists(res.data ?? []);
        } catch (err) {
            console.error('Error fetching lists:', err);
            setAllLists([]);
        } finally {
            setLoading(false);
        }
    }, [ownerId]);

    useEffect(() => {
        if (isOpen) {
            setSelectedListIds(new Set());
            fetchLists();
            setShowCreate(false);
            setNewName('');
            setNewDescription('');
        }
    }, [isOpen, fetchLists]);

    const toggleList = (listId: string) => {
        setSelectedListIds((prev) => {
            const next = new Set(prev);
            if (next.has(listId)) {
                next.delete(listId);
            } else {
                next.add(listId);
            }
            return next;
        });
    };

    const handleSave = async () => {
        if (selectedListIds.size === 0 || itemIds.length === 0) return;
        setSaving(true);
        try {
            const api = await createAxiosInstance();
            const listParams = ownerId ? {ownerId} : {};
            for (const listId of Array.from(selectedListIds)) {
                for (const itemId of itemIds) {
                    await api.post(`/lists/${listId}/items/${itemId}`, {}, {params: listParams});
                }
            }
            const listsCount = selectedListIds.size;
            showToast(
                `${pluralItems(itemIds.length)} — добавлено в ${listsCount === 1 ? 'список' : pluralLists(listsCount)}`,
                'success',
            );
            onSuccess();
            onClose();
        } catch (err) {
            console.error('Error adding items to lists:', err);
            showToast('Не удалось добавить в список', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleCreate = async () => {
        if (!newName.trim()) return;
        setCreating(true);
        try {
            const api = await createAxiosInstance();
            const res = await api.post<{ id: string }>('/lists', {
                name: newName.trim(),
                description: newDescription.trim() || null
            }, {params: ownerId ? {ownerId} : {}});
            const newId = res.data.id;
            setAllLists((prev) => [...prev, {id: newId, name: newName.trim()}]);
            setSelectedListIds((prev) => new Set(Array.from(prev).concat(newId)));
            setShowCreate(false);
            setNewName('');
            setNewDescription('');
        } catch (err) {
            console.error('Error creating list:', err);
        } finally {
            setCreating(false);
        }
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            title={`Добавить в список (${pluralItems(itemIds.length)})`}
            busy={saving}
            data-testid="bulk-add-to-lists-modal"
            panelClassName="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[80vh] flex flex-col outline-none"
            bodyClassName="flex-1 overflow-y-auto p-4"
            footer={
                <button
                    onClick={handleSave}
                    data-testid="bulk-lists-submit"
                    disabled={saving || selectedListIds.size === 0}
                    className="btn-primary"
                >
                    {saving ? 'Сохранение...' : 'Добавить'}
                </button>
            }
        >
                    {loading ? (
                        <div className="flex justify-center py-8">
                            <Spinner/>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {allLists.map((list) => (
                                <label
                                    key={list.id}
                                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                                >
                                    <input
                                        type="checkbox"
                                        data-testid={`bulk-lists-checkbox-${list.id}`}
                                        checked={selectedListIds.has(list.id)}
                                        onChange={() => toggleList(list.id)}
                                        className="rounded"
                                    />
                                    <span className="text-gray-900 dark:text-gray-100">{list.name}</span>
                                </label>
                            ))}
                            {showCreate ? (
                                <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-600 space-y-3">
                                    <input
                                        type="text"
                                        data-testid="bulk-lists-create-name"
                                        value={newName}
                                        onChange={(e) => setNewName(e.target.value)}
                                        className="input-field w-full"
                                        placeholder="Название списка"
                                        autoFocus
                                    />
                                    <textarea
                                        value={newDescription}
                                        onChange={(e) => setNewDescription(e.target.value)}
                                        className="input-field w-full"
                                        rows={2}
                                        placeholder="Описание (необязательно)"
                                    />
                                    <div className="flex gap-2">
                                        <button
                                            onClick={handleCreate}
                                            data-testid="bulk-lists-create-submit"
                                            disabled={!newName.trim() || creating}
                                            className="btn-primary"
                                        >
                                            {creating ? 'Создание...' : 'Создать'}
                                        </button>
                                        <button
                                            data-testid="bulk-lists-create-cancel"
                                            onClick={() => {
                                                setShowCreate(false);
                                                setNewName('');
                                                setNewDescription('');
                                            }}
                                            className="btn-secondary"
                                        >
                                            Отмена
                                        </button>
                                    </div>
                                </div>
                            ) : canCreateList ? (
                                <button
                                    data-testid="bulk-lists-create-toggle"
                                    onClick={() => setShowCreate(true)}
                                    className="flex items-center gap-2 w-full p-2 rounded-lg text-blue-600 dark:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                                >
                                    <PlusIcon className="w-5 h-5"/>
                                    Создать новый список
                                </button>
                            ) : null}
                        </div>
                    )}
        </Dialog>
    );
};

export default BulkAddToListsModal;
